import React from 'react';
import { 
  Snackbar, 
  Alert, 
  AlertTitle,
  IconButton,
  Fade
} from '@mui/material';
import type { FadeProps } from '@mui/material';
import { Close } from '@mui/icons-material';
import './Toast.css';

export type ToastType = 'success' | 'error' | 'warning' | 'info';
export type ToastPosition = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';

export interface ToastData {
  id: string;
  message: string;
  title?: string;
  type: ToastType;
  duration?: number;
  position?: ToastPosition;
  persistent?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastProps extends ToastData {
  open: boolean;
  onClose: (id: string) => void;
}

// Fade transition component
function FadeTransition(props: FadeProps) {
  return <Fade {...props} timeout={{ enter: 300, exit: 200 }} />;
}

const Toast: React.FC<ToastProps> = ({
  id,
  open,
  message,
  title,
  type,
  duration = 4000,
  position = 'bottom-center',
  persistent = false,
  action,
  onClose
}) => {
  const getAnchorOrigin = () => {
    const [vertical, horizontal] = position.split('-') as ['top' | 'bottom', 'left' | 'center' | 'right'];
    return {
      vertical,
      horizontal: horizontal === 'center' ? 'center' as const : horizontal
    };
  };

  const getSnackbarSx = () => {
    const isBottom = position.startsWith('bottom');
    if (isBottom) {
      return {
        bottom: '2px !important', // Very close to bottom (CSS will handle the actual positioning)
      };
    }
    return {};
  };

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway' && persistent) {
      return;
    }
    onClose(id);
  };

  const actionButtons = (
    <>
      {action && (
        <IconButton
          size="small"
          aria-label={action.label}
          color="inherit"
          onClick={action.onClick}
          sx={{ mr: 1 }}
        >
          {action.label}
        </IconButton>
      )}
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={() => handleClose()}
      >
        <Close fontSize="small" />
      </IconButton>
    </>
  );

  return (
    <Snackbar
      open={open}
      autoHideDuration={persistent ? null : duration}
      onClose={handleClose}
      anchorOrigin={getAnchorOrigin()}
      TransitionComponent={FadeTransition}
      className={`toast toast--${type} toast--${position}`}
      sx={getSnackbarSx()}
      key={id} // Add key to ensure proper re-rendering
    >
      <Alert
        onClose={() => handleClose()}
        severity={type}
        variant="filled"
        action={actionButtons}
        className="toast__alert"
        sx={{
          width: '100%',
          alignItems: 'flex-start',
          '& .MuiAlert-icon': {
            fontSize: '1.2rem'
          }
        }}
      >
        {title && <AlertTitle className="toast__title">{title}</AlertTitle>}
        {message}
      </Alert>
    </Snackbar>
  );
};

export default Toast;
